import { Link } from 'react-router-dom';
import { calculateAge } from '@/lib/age';
import type { Patient } from '@/models/patient';
import { useActsStore } from '@/stores/acts';

const NINETY_DAYS_MS = 90 * 24 * 60 * 60 * 1000;

function isExpiredSoon(exp: string): boolean {
  if (!exp) return false;
  const t = new Date(exp).getTime();
  return Number.isFinite(t) && t - Date.now() < NINETY_DAYS_MS;
}

// Mirrors Flutter's SmallAgeBox: orange = active, green = réglé/oriented,
// red = flagged. 40×40 with rounded corners.
function AgeBadge({ patient }: { patient: Patient }) {
  const age = calculateAge(patient.dob);
  const bg = patient.flagged
    ? 'bg-red-500'
    : patient.status === 'active'
      ? 'bg-brand-orange'
      : 'bg-brand-green';
  return (
    <div
      className={`shrink-0 w-12 h-12 rounded-xl ${bg} flex flex-col items-center justify-center text-[11px] font-semibold leading-tight text-white`}
    >
      <span>
        {age != null ? age : '—'} {patient.sex || 'H'}
      </span>
      {patient.civ && <span>{patient.civ.toUpperCase()}</span>}
    </div>
  );
}

export function PatientListTile({ patient }: { patient: Patient }) {
  const billedFromStore = useActsStore(
    (s) => s.totalPatientsBilled[patient.id],
  );
  const billed =
    billedFromStore != null ? String(billedFromStore) : patient.amountBilled || '0';
  const expWarn = isExpiredSoon(patient.healthCardExp);

  return (
    <Link
      to={`/patient/${patient.id}`}
      className="block bg-brand-dark rounded-2xl shadow-card px-3 py-3"
    >
      <div className="flex items-center gap-3">
        <AgeBadge patient={patient} />

        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-1">
            <span className="font-semibold truncate max-w-[120px]">
              {patient.lastName || 'Patient'}
            </span>
            {patient.firstName && (
              <span className="text-sm text-white/70 truncate">
                {patient.firstName}
              </span>
            )}
            {expWarn && (
              <span className="text-[10px] text-amber-300 ml-auto">
                RAMQ exp
              </span>
            )}
          </div>
          {patient.dx && (
            <p className="text-xs text-white/85 truncate">{patient.dx}</p>
          )}
          {patient.notes && (
            <p className="text-xs text-white/55 truncate">{patient.notes}</p>
          )}
          {patient.transferredToUid && (
            <p className="text-[10px] text-brand-green/90 truncate mt-0.5">
              ↗ Transféré à un collègue
            </p>
          )}
        </div>

        <div className="text-right shrink-0">
          <p className="text-[10px] text-white/60 leading-none">Facturé</p>
          <p className="text-base font-semibold leading-tight mt-1">
            {billed} $
          </p>
        </div>
      </div>
    </Link>
  );
}
